/**
 * MediaLibraryPage — media browse, upload, and delete interface.
 *
 * Layout:
 * 1. Page header with title
 * 2. Drag-and-drop upload zone (click or drag a file)
 * 3. MediaGrid showing all uploaded files
 *
 * Upload uses raw fetch with FormData via useUploadMedia hook —
 * NOT the api() helper which sets Content-Type: application/json.
 */

import { useRef, useState, useCallback, type DragEvent, type ChangeEvent } from 'react'
import { MediaGrid } from '@/components/cms/MediaGrid'
import { useMedia, useUploadMedia, useDeleteMedia } from '@/hooks/useCms'

// ─── Drop Zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void
  isUploading: boolean
}

function DropZone({ onFile, isUploading }: DropZoneProps) {
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDraggingOver(false)
      const file = e.dataTransfer.files[0]
      if (file) onFile(file)
    },
    [onFile],
  )

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        onFile(file)
        // Reset input so the same file can be re-selected if needed
        e.target.value = ''
      }
    },
    [onFile],
  )

  const handleClick = useCallback(() => {
    if (!isUploading) fileInputRef.current?.click()
  }, [isUploading])

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={[
        'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors',
        isDraggingOver
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-gray-300 bg-gray-50 text-gray-500 hover:border-gray-400 hover:bg-gray-100',
        isUploading ? 'cursor-not-allowed opacity-60' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/svg+xml,application/pdf"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {isUploading ? (
        <p className="text-sm font-medium">Subiendo archivo...</p>
      ) : (
        <>
          <svg
            className="mb-3 h-10 w-10 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
            />
          </svg>
          <p className="text-sm font-medium">Arrastra un archivo aqui o haz clic para seleccionar</p>
          <p className="mt-1 text-xs text-gray-400">
            JPEG, PNG, WebP, SVG, PDF — maximo 10 MB
          </p>
        </>
      )}
    </div>
  )
}

// ─── MediaLibraryPage ─────────────────────────────────────────────────────────

export function MediaLibraryPage() {
  const { data: items = [], isLoading, isError } = useMedia()
  const uploadMutation = useUploadMedia()
  const deleteMutation = useDeleteMedia()

  const [uploadError, setUploadError] = useState<string | null>(null)

  function handleFile(file: File) {
    setUploadError(null)
    uploadMutation.mutate(file, {
      onError: (err) => {
        setUploadError(err.message || 'Error al subir el archivo')
      },
    })
  }

  function handleDelete(id: string) {
    deleteMutation.mutate(id)
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500">CMS</p>
        <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
        <p className="mt-1 text-sm text-gray-600">
          Sube y administra imagenes y documentos para usar en el contenido del sitio.
        </p>
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {uploadError}
          <button
            onClick={() => setUploadError(null)}
            className="ml-2 underline"
            type="button"
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Drop zone */}
      <DropZone onFile={handleFile} isUploading={uploadMutation.isPending} />

      {/* Media grid */}
      <div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-gray-500">
            Cargando archivos...
          </div>
        ) : isError ? (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            Error al cargar los archivos. Intenta recargar la pagina.
          </div>
        ) : (
          <MediaGrid items={items} onDelete={handleDelete} />
        )}
      </div>
    </div>
  )
}
